import type { Region } from '~/types/locale'
import { CountryCode, Culture, FlagIcon, Currency, RegionCode, Language } from '~/types/locale'

export const additionalFrDepartments =
{
  98: 'Outre-mer', 99: 'Abroad'
}

/**
 * Field under country apply as a patch over fields from region (replace by name)
 * If you want to remove a field already applied in region, add the same field with `type: 'remove'` under country
 */
export const regions: Region[] = [
  {
    name: 'North America',
    regionCode: RegionCode.NA,
    phoneNumberFormat: '(###) ### - ####',
    phoneNumberRules: 'required|su_phone|su_min:10|su_max:18',
    demoFinderRules: 'min:3',
    catalogMailing: true,
    addressConfig: {
      fields: [
        { name: 'addressLine1', type: 'autocomplete', line: 1, rules: { required: true, max: 30, su_cc: { reverse: true } } },
        { name: 'addressLine2', type: 'text', line: 2, rules: { required: false, max: 30, su_cc: { reverse: true } } },
        { name: 'city', type: 'text', line: 4, rules: { required: true, max: 25, su_address: true, su_cc: { reverse: true } } },
        { name: 'region', type: 'state', line: 5, sequence: 0, rules: { required: true, max: 10, su_cc: { reverse: true } } },
        { name: 'postalCode', type: 'text', line: 5, sequence: 1, rules: 'required|su_postalcode|max:10|min:5', mask: '*****-****' }
      ],
      validate: false,
      display: [
        ['firstName', 'lastName'],
        'addressLine1',
        'addressLine2',
        ['city', 'region', 'postalCode']
      ]
    },
    idaPersonalInfo: [
      { name: 'birthday', type: 'birthday', rules: { required: true }, sequence: 1 },
      { name: 'gender', type: 'gender', sequence: 2 },
      { name: 'secondaryLanguage', type: 'language', rules: { su_required_object: false }, sequence: 4 }
    ],
    enabledFeatures: ['autocomplete'],
    countries: [
      {
        name: 'United States',
        culture: Culture.EN_US,
        countryCode: CountryCode.US,
        displayCode: CountryCode.US,
        icon: FlagIcon.US,
        active: true,
        currency: Currency.USD, // todo get state values for all countries
        states: [],
        stateFieldName: 'State',
        idaPersonalInfo: [
          // Nine digits; su_min counted the mask's two spaces, which it no longer does. AB#64479
          { name: 'identification', type: 'text', rules: { required: true, su_digits: 9 }, mask: '### ## ####', sequence: 3 },
          { name: 'secondaryLanguage', type: 'remove' }
        ],
        idaSupportingTaxMask: '### ## ####',
        availableLanguages: [Language.EN]
      },
      {
        name: 'Canada',
        culture: Culture.EN_CA,
        countryCode: CountryCode.CA,
        displayCode: CountryCode.CA,
        icon: FlagIcon.CA,
        active: true,
        currency: Currency.CAD,
        states: [],
        stateFieldName: 'Province',
        addressConfig: {
          fields: [
            { name: 'postalCode', type: 'text', line: 5, sequence: 1, rules: 'required|su_postalcode|max:7|min:7', mask: '*** ***' }
          ]
        },
        idaPersonalInfo: [
          { name: 'identification', type: 'text', rules: { required: true, su_digits: 9 }, mask: '### ### ###', sequence: 3 }
        ],
        idaSupportingTaxMask: '### ### ###',
        availableLanguages: [Language.EN, Language.FR]
      },
      {
        name: 'Canada',
        culture: Culture.FR_CA,
        countryCode: CountryCode.CA,
        displayCode: CountryCode.CA,
        icon: FlagIcon.CA,
        active: true,
        currency: Currency.CAD,
        states: [],
        stateFieldName: 'Province',
        addressConfig: {
          fields: [
            { name: 'postalCode', type: 'text', line: 5, sequence: 1, rules: 'required|su_postalcode|max:7|min:7', mask: '*** ***' }
          ]
        },
        idaPersonalInfo: [
          { name: 'identification', type: 'text', rules: { required: true, su_digits: 9 }, mask: '### ### ###', sequence: 3 }
        ],
        idaSupportingTaxMask: '### ### ###',
        availableLanguages: [Language.FR, Language.EN]
      }
    ]
  },
  {
    name: 'Europe',
    regionCode: RegionCode.EU,
    phoneNumberFormat: '+FFFFFFFFFFFFFFF',
    phoneNumberRules: 'required|su_phone|min:7|max:18',
    demoFinderRules: 'min:3',
    catalogMailing: false,
    addressConfig: {
      fields: [
        { name: 'addressLine1', type: 'autocomplete', line: 1, rules: { required: true, max: 30, su_cc: { reverse: true } } },
        { name: 'addressLine2', type: 'text', line: 2, rules: { required: false, max: 30, su_cc: { reverse: true } } },
        { name: 'addressLine3', type: 'text', line: 3, rules: { required: false, max: 30, su_cc: { reverse: true } } },
        { name: 'city', type: 'text', line: 4, sequence: 1, rules: { required: true, max: 25, su_address: true, su_cc: { reverse: true } } }
      ],
      validate: false,
      display: [
        ['firstName', 'lastName'],
        'addressLine1',
        'addressLine2',
        'addressLine3',
        ['postalCode', 'city']
      ]
    },
    idaPersonalInfo: [
      { name: 'birthday', type: 'birthday', rules: { required: true }, sequence: 1 },
      { name: 'gender', type: 'gender', sequence: 2 },
      { name: 'secondaryLanguage', type: 'language', rules: { su_required_object: false }, sequence: 4 }
    ],
    enabledFeatures: ['paypal'],
    idaTaxInformation: true,
    countries: [
      {
        name: 'United Kingdom',
        culture: Culture.EN_GB,
        countryCode: CountryCode.GB,
        vatRules: 'su_gb_vat',
        displayCode: 'UK',
        icon: FlagIcon.GB,
        active: true,
        currency: Currency.GBP,
        addressConfig: {
          fields: [
            { name: 'county', type: 'text', line: 5, sequence: 0, rules: { required: true, max: 40, su_cc: { reverse: true } } },
            { name: 'postalCode', type: 'text', line: 5, sequence: 1, rules: 'required|min:6|max:8|su_postalcode|su_ukpostalcode' }
          ],
          validate: false,
          display: [
            ['firstName', 'lastName'],
            'addressLine1',
            'addressLine2',
            'addressLine3',
            'city',
            'county',
            'postalCode'
          ]
        },
        enabledFeatures: ['autocomplete', 'paypal'],
        availableLanguages: [Language.EN]
      },
      {
        name: 'Deutschland',
        culture: Culture.DE_DE,
        phoneNumberFormat: '+FFFFFFFFFFFFFFFFF',
        countryCode: CountryCode.DE,
        vatRules: 'su_de_vat',
        demoFinderRules: 'min:2',
        displayCode: CountryCode.DE,
        icon: FlagIcon.DE,
        active: true,
        currency: Currency.EUR,
        states: [],
        addressConfig: {
          fields: [
            { name: 'postalCode', type: 'text', line: 4, sequence: 0, rules: 'required|length:5', mask: '#####' }
          ],
          validate: false
        },
        availableLanguages: [Language.DE]
      },
      {
        name: 'France',
        culture: Culture.FR_FR,
        phoneNumberRules: 'required|su_phone|min:10|max:18',
        countryCode: CountryCode.FR,
        vatRules: 'su_fr_vat',
        demoFinderRules: 'min:2',
        displayCode: CountryCode.FR,
        icon: FlagIcon.FR,
        active: true,
        currency: Currency.EUR,
        states: [],
        addressConfig: {
          fields: [
            { name: 'postalCode', type: 'text', line: 4, sequence: 0, rules: 'required|length:5|su_frpostalcode|numeric', mask: '#####' }
          ],
          validate: false
        },
        idaPersonalInfo: [
          { name: 'birthday', type: 'birthday', rules: { required: true }, sequence: 1 },
          { name: 'identification', type: 'text', rules: { required: true, su_digits: 15 }, mask: '# ## ## ## ### ### ##', sequence: 3 },
          { name: 'birthCity', type: 'text', rules: { required: true, max: 25 }, sequence: 4 },
          { name: 'birthDepartment', type: 'combobox', rules: { su_required_object: true }, sequence: 5 },
          { name: 'secondaryLanguage', type: 'language', rules: { su_required_object: false }, sequence: 6 },
          { name: 'birthCountry', type: 'text', rules: { required: true }, sequence: 7 },
          { name: 'nationality', type: 'text', rules: { required: true }, sequence: 8 }
        ],
        availableLanguages: [Language.FR]
      },
      {
        name: 'Österreich',
        culture: Culture.DE_AT,
        countryCode: CountryCode.AT,
        vatRules: 'su_at_vat',
        displayCode: CountryCode.AT,
        icon: FlagIcon.AT,
        active: true,
        currency: Currency.EUR,
        states: [],
        addressConfig: {
          fields: [
            { name: 'postalCode', type: 'text', line: 4, sequence: 0, rules: 'required|length:4|numeric', mask: '####' }
          ],
          validate: false
        },
        idaPersonalInfo: [
          { name: 'identification', type: 'text', rules: { required: true, su_min: 10 }, mask: '#### ######', sequence: 3 }
        ],
        availableLanguages: [Language.DE]
      },
      {
        name: 'Netherlands',
        culture: Culture.NL_NL,
        countryCode: CountryCode.NL,
        vatRules: 'su_nl_vat',
        displayCode: CountryCode.NL,
        icon: FlagIcon.NL,
        active: true,
        currency: Currency.EUR,
        states: [],
        addressConfig: {
          fields: [
            { name: 'postalCode', type: 'text', line: 4, sequence: 0, rules: 'required|su_nl_postal', mask: '#### @@' }
          ],
          validate: false
        },
        availableLanguages: [Language.NL]
      },
      {
        name: 'Belgium',
        culture: Culture.NL_BE,
        countryCode: CountryCode.BE,
        phoneNumberRules: 'required|su_phone|su_min:8|su_max:10',
        vatRules: 'su_be_vat',
        displayCode: CountryCode.BE,
        icon: FlagIcon.BE,
        active: true,
        currency: Currency.EUR,
        states: [],
        addressConfig: {
          fields: [
            { name: 'postalCode', type: 'text', line: 4, sequence: 0, rules: 'required|su_be_postal', mask: '####' }
          ],
          validate: false
        },
        enabledFeatures: ['address-validate', 'paypal', 'autocomplete'],
        availableLanguages: [Language.NL, Language.DE, Language.FR]
      },
      {
        name: 'Belgium',
        culture: Culture.FR_BE,
        countryCode: CountryCode.BE,
        phoneNumberRules: 'required|su_phone|su_min:8|su_max:10',
        vatRules: 'su_be_vat',
        displayCode: CountryCode.BE,
        icon: FlagIcon.BE,
        active: true,
        currency: Currency.EUR,
        states: [],
        addressConfig: {
          fields: [
            { name: 'postalCode', type: 'text', line: 4, sequence: 0, rules: 'required|su_be_postal', mask: '####' }
          ],
          validate: false
        },
        enabledFeatures: ['address-validate', 'paypal', 'autocomplete'],
        availableLanguages: [Language.FR, Language.NL, Language.DE]
      },
      {
        name: 'Belgium',
        culture: Culture.DE_BE,
        countryCode: CountryCode.BE,
        phoneNumberRules: 'required|su_phone|su_min:8|su_max:10',
        vatRules: 'su_be_vat',
        displayCode: CountryCode.BE,
        icon: FlagIcon.BE,
        active: true,
        currency: Currency.EUR,
        states: [],
        addressConfig: {
          fields: [
            { name: 'postalCode', type: 'text', line: 4, sequence: 0, rules: 'required|su_be_postal', mask: '####' }
          ],
          validate: false
        },
        enabledFeatures: ['address-validate', 'paypal', 'autocomplete'],
        availableLanguages: [Language.DE, Language.NL, Language.FR]
      },
      {
        name: 'Ireland',
        culture: Culture.EN_IE,
        countryCode: CountryCode.IE,
        phoneNumberRules: 'required|su_phone|su_min:7|su_max:10',
        vatRules: 'su_ie_vat',
        displayCode: CountryCode.IE,
        icon: FlagIcon.IE,
        active: true,
        currency: Currency.EUR,
        states: [],
        addressConfig: {
          fields: [
            { name: 'postalCode', type: 'text', line: 5, sequence: 1, rules: 'required|min:7|max:8|su_iepostalcode' },
            { name: 'county', type: 'text', line: 5, sequence: 0, rules: { required: true, max: 40, su_cc: { reverse: true } } }
          ],
          validate: false
        },
        enabledFeatures: ['address-validate', 'paypal', 'autocomplete'],
        availableLanguages: [Language.EN]
      }
    ]
  },
  {
    name: 'South Pacific',
    regionCode: RegionCode.SP,
    phoneNumberFormat: 'FFFFFFFFFFFFFF',
    phoneNumberRules: 'required|su_phone|min:7|max:18',
    demoFinderRules: 'min:3',
    catalogMailing: false,
    addressConfig: {
      fields: [
        { name: 'addressLine1', type: 'autocomplete', line: 1, rules: { required: true, max: 30, su_cc: { reverse: true } } },
        { name: 'addressLine2', type: 'text', line: 2, rules: { required: false, max: 30, su_cc: { reverse: true } } },
        { name: 'city', type: 'text', line: 5, sequence: 0, rules: { required: true, max: 25, su_address: true, su_cc: { reverse: true } } },
        { name: 'postalCode', type: 'text', line: 5, sequence: 1, rules: 'required|length:4', mask: '####' }
      ],
      validate: false,
      display: [
        ['firstName', 'lastName'],
        'addressLine1',
        'addressLine2',
        ['suburb', 'region', 'postalCode']
      ]
    },
    idaPersonalInfo: [
      { name: 'birthday', type: 'birthday', rules: { required: true }, sequence: 1 },
      { name: 'gender', type: 'gender', sequence: 2 },
      { name: 'identification', type: 'text', rules: { required: false }, sequence: 3 },
      { name: 'secondaryLanguage', type: 'language', rules: { su_required_object: false }, sequence: 4 }
    ],
    enabledFeatures: ['paypal'],
    idaTaxInformation: true,
    countries: [
      {
        name: 'Australia',
        culture: Culture.EN_AU,
        countryCode: CountryCode.AU,
        vatRules: 'su_au_gst',
        catalogMailing: true,
        phoneNumberRules: 'required|su_phone|min:7|max:18|su_au_phone',
        displayCode: CountryCode.AU,
        icon: FlagIcon.AU,
        active: true,
        currency: Currency.AUD,
        states: [],
        stateFieldName: 'State',
        addressConfig: {
          fields: [
            { name: 'city', type: 'remove' },
            { name: 'suburb', type: 'text', line: 4, sequence: 0, rules: { required: true, max: 25, su_address: true, su_cc: { reverse: true } } },
            { name: 'region', type: 'state', line: 5, sequence: 0, rules: { required: true, max: 10, su_cc: { reverse: true } } },
            { name: 'postalCode', type: 'text', line: 5, sequence: 1, rules: 'required|length:4|numeric' }
          ],
          validate: false
        },
        idaPersonalInfo: [
          { name: 'birthday', type: 'birthday', rules: { required: true }, sequence: 1 },
          { name: 'gender', type: 'gender', sequence: 2 },
          { name: 'identification', type: 'text', rules: { required: false, su_digits: 11 }, sequence: 3 },
          { name: 'driversLicense', type: 'text', rules: 'required_if:driversLicenseState', sequence: 4 },
          { name: 'driversLicenseState', type: 'state', rules: 'required_if:driversLicense|max:3', sequence: 5 },
          { name: 'secondaryLanguage', type: 'remove' }
        ],
        enabledFeatures: ['autocomplete', 'address-validate'],
        availableLanguages: [Language.EN]
      },
      {
        name: 'New Zealand',
        culture: Culture.EN_NZ,
        countryCode: CountryCode.NZ,
        phoneNumberRules: 'required|su_phone|su_min:9|su_max:11',
        catalogMailing: false,
        displayCode: CountryCode.NZ,
        icon: FlagIcon.NZ,
        active: true,
        currency: Currency.NZD,
        states: [],
        idaPersonalInfo: [
          { name: 'identification', type: 'text', rules: { required: true }, mask: '###-###-###', sequence: 3 },
          { name: 'secondaryLanguage', type: 'remove' }
        ],
        idaSupportingTaxMask: '###-###-###',
        addressConfig: {
          fields: [
            { name: 'suburb', type: 'text', line: 4, rules: { max: 25, su_address: true, su_cc: { reverse: true } } },
            { name: 'postalCode', type: 'text', line: 5, sequence: 1, rules: 'required|length:4|numeric' }
          ],
          validate: false,
          display: [
            ['firstName', 'lastName'],
            'addressLine1',
            'addressLine2',
            'suburb',
            ['city', 'postalCode']
          ]
        },
        availableLanguages: [Language.EN]
      }
    ]
  }
]
